import * as React from 'react';
import { useContext, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ADMIN_VIEWS, ADMIN_ROUTES } from "../../utils/constant";
import {
    Dashboard,
    AccountTree,
    People,
    Logout,
    Menu,
    Close,
    Groups3,
    ReceiptLong,
    Mail
} from '@mui/icons-material';
import { Tooltip, Avatar, IconButton } from '@mui/material';
import { LoginContext } from '../../contexts/LoginContext';
import '../../styles/AdminSidebar.css';
import { useUnreadCount } from '../../api/messages/useUnreadCount';
import { useLogout } from '../../hooks/useLogout';


interface MenuItem {
    view: number;
    icon: React.ReactElement;
    text: string;
    route: string;
    badge?: number;
}

interface MenuGroup {
    title: string;
    items: MenuItem[];
}

interface AdminGlobalMenuProps {
    selectedMenu: (view: number) => void;
}

const AdminGlobalMenu = ({ selectedMenu }: AdminGlobalMenuProps) => {
    const location = useLocation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const { currentUser } = useContext(LoginContext);
    const { data: unreadCount = 0 } = useUnreadCount();
    const logout = useLogout();

    // Handlers pour l'expansion au hover
    const handleMouseEnter = () => setIsHovered(true);
    const handleMouseLeave = () => setIsHovered(false);

    // Déterminer la vue active basée sur l'URL
    const getActiveView = () => {
        const path = location.pathname;
        if (path.includes('/admin/messaging')) return ADMIN_VIEWS.MESSAGING_VIEW;
        if (path.includes('/admin/transactions')) return ADMIN_VIEWS.TRANSACTION_HISTORY_VIEW;
        if (path.includes('/admin/projects')) return ADMIN_VIEWS.PROJECT_LIST_VIEW;
        if (path.includes('/admin/users')) return ADMIN_VIEWS.USER_LIST_VIEW;
        if (path.includes('/admin/groups/new')) return ADMIN_VIEWS.NEW_GROUP_VIEW;
        if (path.includes('/admin/groups')) return ADMIN_VIEWS.GROUP_LIST_VIEW;
        if (path.includes('/admin/new-project')) return ADMIN_VIEWS.NEW_PROJECT_VIEW;
        if (path.includes('/admin/new-user')) return ADMIN_VIEWS.NEW_USER_VIEW;
        if (path.includes('/admin/new-cash-in')) return ADMIN_VIEWS.NEW_CASH_IN_VIEW;
        if (path.includes('/admin/new-cash-out')) return ADMIN_VIEWS.NEW_CASH_OUT_VIEW;
        return ADMIN_VIEWS.DASHBOARD_VIEW;
    };

    const activeView = getActiveView();

    const menuItems: MenuGroup[] = [
        {
            title: 'Principal',
            items: [
                {
                    view: ADMIN_VIEWS.DASHBOARD_VIEW,
                    icon: <Dashboard />,
                    text: 'Dashboard',
                    route: ADMIN_ROUTES.DASHBOARD
                },
                {
                    view: ADMIN_VIEWS.MESSAGING_VIEW,
                    icon: <Mail />,
                    text: 'Messages',
                    route: ADMIN_ROUTES.MESSAGING,
                    badge: unreadCount
                }
            ]
        },
        {
            title: 'Gestion',
            items: [
                {
                    view: ADMIN_VIEWS.PROJECT_LIST_VIEW,
                    icon: <AccountTree />,
                    text: 'Projets',
                    route: ADMIN_ROUTES.PROJECTS
                },
                {
                    view: ADMIN_VIEWS.USER_LIST_VIEW,
                    icon: <People />,
                    text: 'Utilisateurs',
                    route: ADMIN_ROUTES.USERS
                },
                {
                    view: ADMIN_VIEWS.GROUP_LIST_VIEW,
                    icon: <Groups3 />,
                    text: 'Groupes',
                    route: ADMIN_ROUTES.GROUPS
                }
            ]
        },
        {
            title: 'Finances',
            items: [
                {
                    view: ADMIN_VIEWS.TRANSACTION_HISTORY_VIEW,
                    icon: <ReceiptLong />,
                    text: 'Transactions',
                    route: ADMIN_ROUTES.TRANSACTION_HISTORY
                }
            ]
        }
    ];

    const handleMenuClick = (view: number) => {
        selectedMenu(view);
        setIsMobileMenuOpen(false);
    };

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    return (
        <>
            {/* Bouton menu mobile */}
            <IconButton
                className="admin-mobile-menu-toggle"
                onClick={toggleMobileMenu}
                sx={{
                    position: 'fixed',
                    top: '1rem',
                    left: '1rem',
                    zIndex: 1001,
                    display: { xs: 'flex', lg: 'none' },
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    color: '#667eea'
                }}
            >
                {isMobileMenuOpen ? <Close /> : <Menu />}
            </IconButton>

            {/* Overlay mobile */}
            <div
                className={`admin-sidebar-overlay ${isMobileMenuOpen ? 'open' : ''}`}
                onClick={() => setIsMobileMenuOpen(false)}
            />

            {/* Menu latéral */}
            <div
                className={`admin-sidebar ${isMobileMenuOpen ? 'open' : ''} ${isHovered ? 'expanded' : ''}`}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {/* Section utilisateur */}
                <div className="admin-sidebar-user">
                    <div className="admin-user-info">
                        <Avatar className="admin-user-avatar">
                            {currentUser?.firstname?.charAt(0) || 'A'}
                        </Avatar>
                        <div className="admin-user-details">
                            <h3 className="admin-user-name">
                                {currentUser?.firstname} {currentUser?.lastname}
                            </h3>
                            <p className="admin-user-role">Administrateur</p>
                        </div>
                    </div>
                </div>

                {/* Navigation */}
                <nav className="admin-sidebar-nav">
                    {menuItems.map((group, groupIndex) => (
                        <div key={groupIndex} className="admin-menu-group">
                            <h4 className="admin-menu-group-title">{group.title}</h4>
                            {group.items.map((item) => (
                                <Tooltip
                                    key={item.view}
                                    title={!isHovered ? item.text : ''}
                                    placement="right"
                                    arrow
                                    disableHoverListener={isHovered}
                                >
                                    <div
                                        className={`admin-menu-item ${activeView === item.view ? 'active' : ''}`}
                                        onClick={() => handleMenuClick(item.view)}
                                    >
                                        <div className="admin-menu-icon">
                                            {item.icon}
                                        </div>
                                        <span className="admin-menu-text">{item.text}</span>
                                        {item.badge !== undefined && item.badge > 0 && (
                                            <div className="admin-menu-badge">
                                                {item.badge}
                                            </div>
                                        )}
                                    </div>
                                </Tooltip>
                            ))}
                        </div>
                    ))}
                </nav>

                {/* Footer avec déconnexion */}
                <div className="admin-sidebar-footer">
                    <Tooltip
                        title={!isHovered ? "Déconnexion" : ''}
                        placement="right"
                        arrow
                        disableHoverListener={isHovered}
                    >
                        <div className="admin-logout-button" onClick={logout}>
                            <div className="admin-menu-icon">
                                <Logout />
                            </div>
                            <span className="admin-menu-text">Déconnexion</span>
                        </div>
                    </Tooltip>
                </div>
            </div>
        </>
    );
};

export default AdminGlobalMenu;
